import { useState, useCallback, useEffect, useRef, useMemo } from 'react';
import { userFolderService } from '../services/userFolderService';
import { isPermissionDenied } from '../utils/grpcErrors';
import { useAccessSettlingRetry } from './useAccessSettlingRetry';
export const useUserFolders = ({ projectId, isAuthenticated }) => {
    const [folders, setFolders] = useState([]);
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState(null);
    const [denied, setDenied] = useState(false);
    const [folderVisibility, setFolderVisibility] = useState({});
    const [folderCollapsed, setFolderCollapsed] = useState({});
    const [artifactVisibility, setArtifactVisibility] = useState({});
    const [artifactOpacity, setArtifactOpacity] = useState({});
    // Pre-compute effective folder visibility once per render. A folder is
    // effectively visible iff its own toggle is on AND every ancestor's toggle
    // is on. We don't mutate ancestor toggle state — cascade is computed.
    // Top-down walk: process parents before children so we can reuse the result.
    const effectiveFolderVisibility = useMemo(() => {
        const map = new Map();
        const byId = new Map(folders.map(f => [f.id, f]));
        const resolve = (folderId) => {
            const cached = map.get(folderId);
            if (cached !== undefined)
                return cached;
            const folder = byId.get(folderId);
            if (!folder)
                return true;
            const own = folderVisibility[folderId] !== false;
            const parentId = folder.parentFolderId;
            const parentVisible = parentId ? resolve(parentId) : true;
            const effective = own && parentVisible;
            map.set(folderId, effective);
            return effective;
        };
        folders.forEach(f => resolve(f.id));
        return map;
    }, [folders, folderVisibility]);
    const derivedVisibility = useMemo(() => {
        const map = new Map();
        folders.forEach(folder => {
            var _a;
            const folderVisible = (_a = effectiveFolderVisibility.get(folder.id)) !== null && _a !== void 0 ? _a : true;
            folder.artifacts.forEach(artifact => {
                var _a;
                map.set(artifact.id, {
                    visible: folderVisible && (artifactVisibility[artifact.id] === true),
                    opacity: (_a = artifactOpacity[artifact.id]) !== null && _a !== void 0 ? _a : 1.0
                });
            });
        });
        return map;
    }, [folders, effectiveFolderVisibility, artifactVisibility, artifactOpacity]);
    // Tree assembly: group by parentFolderId, walk roots, assign depth (1..3).
    // Children within a parent are ordered by sortOrder, matching the flat
    // ordering produced by GetUserFoldersByProjectID.
    const folderTree = useMemo(() => {
        var _a, _b;
        const childrenByParent = new Map();
        for (const f of folders) {
            const key = (_a = f.parentFolderId) !== null && _a !== void 0 ? _a : null;
            const bucket = (_b = childrenByParent.get(key)) !== null && _b !== void 0 ? _b : [];
            bucket.push(f);
            childrenByParent.set(key, bucket);
        }
        for (const bucket of childrenByParent.values()) {
            bucket.sort((a, b) => a.sortOrder - b.sortOrder);
        }
        const build = (parentKey, depth) => {
            var _a;
            const direct = (_a = childrenByParent.get(parentKey)) !== null && _a !== void 0 ? _a : [];
            return direct.map(f => ({
                ...f,
                depth,
                children: build(f.id, depth + 1)
            }));
        };
        return build(null, 1);
    }, [folders]);
    const isAncestorHidden = useCallback((folderId) => {
        var _a, _b;
        const byId = new Map(folders.map(f => [f.id, f]));
        let cursor = (_a = byId.get(folderId)) === null || _a === void 0 ? void 0 : _a.parentFolderId;
        while (cursor) {
            if (folderVisibility[cursor] === false)
                return true;
            cursor = (_b = byId.get(cursor)) === null || _b === void 0 ? void 0 : _b.parentFolderId;
        }
        return false;
    }, [folders, folderVisibility]);
    const fetchedProjectRef = useRef(null);
    const fetchFolders = useCallback(async () => {
        if (!projectId || !isAuthenticated) {
            setFolders([]);
            return;
        }
        setIsLoading(true);
        setError(null);
        try {
            const folderList = await userFolderService.getFolders(projectId);
            const foldersWithArtifacts = await Promise.all(folderList.map(async (folder) => {
                try {
                    const folderWithArtifacts = await userFolderService.getFolder(folder.id);
                    return folderWithArtifacts;
                }
                catch (_a) {
                    return { ...folder, artifacts: [] };
                }
            }));
            setFolders(foldersWithArtifacts);
            // Preserve existing visibility for artifacts already known; default new ones to hidden.
            // This prevents fetchFolders (after save/edit) from blowing away user-set visibility.
            const allArtifacts = foldersWithArtifacts.flatMap(f => f.artifacts);
            setArtifactVisibility(prev => {
                const next = {};
                allArtifacts.forEach(a => {
                    next[a.id] = a.id in prev ? prev[a.id] : false;
                });
                return next;
            });
            fetchedProjectRef.current = projectId;
            setDenied(false);
        }
        catch (err) {
            const message = err instanceof Error ? err.message : 'Failed to load folders';
            console.error('[UserFolders] Error fetching folders:', message);
            setError(message);
            setDenied(isPermissionDenied(err));
        }
        finally {
            setIsLoading(false);
        }
    }, [projectId, isAuthenticated]);
    useEffect(() => {
        if (projectId && isAuthenticated && fetchedProjectRef.current !== projectId) {
            // Drop the previous project's list up front: if this fetch fails, that
            // list must not stay on screen under the new project.
            setFolders([]);
            setDenied(false);
            fetchedProjectRef.current = null;
            fetchFolders();
        }
        else if (!projectId || !isAuthenticated) {
            setFolders([]);
            setDenied(false);
            fetchedProjectRef.current = null;
        }
    }, [projectId, isAuthenticated, fetchFolders]);
    const settling = useAccessSettlingRetry(projectId, denied, fetchFolders);
    const createFolder = useCallback(async (name, color, parentFolderId) => {
        if (!projectId)
            throw new Error('No project selected');
        if (!isAuthenticated)
            throw new Error('User must be authenticated');
        const folder = await userFolderService.createFolder({
            projectId,
            name,
            color: color || '#3B82F6',
            parentFolderId: parentFolderId !== null && parentFolderId !== void 0 ? parentFolderId : undefined
        });
        setFolders(prev => [...prev, { ...folder, artifacts: [] }]);
        setFolderVisibility(prev => ({ ...prev, [folder.id]: true }));
        // Start collapsed. An empty new folder has nothing to show but the
        // dashed "Drop items here" placeholder — that's noise the user didn't
        // ask for. They can click the chevron when they want to peek inside.
        setFolderCollapsed(prev => ({ ...prev, [folder.id]: true }));
        return folder;
    }, [projectId, isAuthenticated]);
    const updateFolder = useCallback(async (folderId, updates) => {
        await userFolderService.updateFolder({ folderId, ...updates });
        setFolders(prev => prev.map(folder => folder.id === folderId
            ? { ...folder, ...updates }
            : folder));
    }, []);
    const deleteFolder = useCallback(async (folderId) => {
        var _a;
        // Walk the subtree locally so the optimistic update matches the server-side
        // CASCADE (parent_folder_id self-ref + user_artifacts.folder_id both have
        // ON DELETE CASCADE in the schema; deleting a folder takes the whole tree).
        const childrenByParent = new Map();
        for (const f of folders) {
            if (f.parentFolderId) {
                const bucket = (_a = childrenByParent.get(f.parentFolderId)) !== null && _a !== void 0 ? _a : [];
                bucket.push(f);
                childrenByParent.set(f.parentFolderId, bucket);
            }
        }
        const subtreeFolderIds = [];
        const subtreeArtifactIds = [];
        const walk = (id) => {
            var _a;
            const folder = folders.find(f => f.id === id);
            if (!folder)
                return;
            subtreeFolderIds.push(folder.id);
            subtreeArtifactIds.push(...folder.artifacts.map(a => a.id));
            for (const child of (_a = childrenByParent.get(id)) !== null && _a !== void 0 ? _a : [])
                walk(child.id);
        };
        walk(folderId);
        await userFolderService.deleteFolder(folderId);
        const folderIdSet = new Set(subtreeFolderIds);
        const artifactIdSet = new Set(subtreeArtifactIds);
        setFolders(prev => prev.filter(f => !folderIdSet.has(f.id)));
        setFolderVisibility(prev => {
            const newState = { ...prev };
            folderIdSet.forEach(id => delete newState[id]);
            return newState;
        });
        setFolderCollapsed(prev => {
            const newState = { ...prev };
            folderIdSet.forEach(id => delete newState[id]);
            return newState;
        });
        setArtifactVisibility(prev => {
            const newState = { ...prev };
            artifactIdSet.forEach(id => delete newState[id]);
            return newState;
        });
        setArtifactOpacity(prev => {
            const newState = { ...prev };
            artifactIdSet.forEach(id => delete newState[id]);
            return newState;
        });
    }, [folders]);
    const createArtifact = useCallback(async (request) => {
        const artifact = await userFolderService.createArtifact(request);
        setFolders(prev => prev.map(folder => folder.id === request.folderId
            ? { ...folder, artifacts: [...folder.artifacts, artifact] }
            : folder));
        setArtifactVisibility(prev => ({ ...prev, [artifact.id]: true }));
        return artifact;
    }, []);
    const reorderArtifacts = useCallback(async (folderId, orderedArtifactIds) => {
        // Optimistic local reorder so the panel updates instantly. The hook caller
        // should snapshot pre-reorder state and pass it to a rollback if this
        // promise rejects (the panel does that today via try/catch + status toast).
        setFolders(prev => prev.map(folder => {
            if (folder.id !== folderId)
                return folder;
            const byId = new Map(folder.artifacts.map(a => [a.id, a]));
            const reordered = orderedArtifactIds
                .map(id => byId.get(id))
                .filter((a) => a !== undefined);
            // Append any artifacts not present in the requested order (defensive
            // against stale lists racing with concurrent creates).
            const seen = new Set(orderedArtifactIds);
            for (const a of folder.artifacts) {
                if (!seen.has(a.id))
                    reordered.push(a);
            }
            return { ...folder, artifacts: reordered };
        }));
        await userFolderService.reorderArtifacts(folderId, orderedArtifactIds);
    }, []);
    const deleteArtifact = useCallback(async (artifactId) => {
        await userFolderService.deleteArtifact(artifactId);
        setFolders(prev => prev.map(folder => ({
            ...folder,
            artifacts: folder.artifacts.filter(a => a.id !== artifactId)
        })));
        setArtifactVisibility(prev => {
            const newState = { ...prev };
            delete newState[artifactId];
            return newState;
        });
        setArtifactOpacity(prev => {
            const newState = { ...prev };
            delete newState[artifactId];
            return newState;
        });
    }, []);
    const toggleFolderVisibility = useCallback((folderId) => {
        setFolderVisibility(prev => ({
            ...prev,
            [folderId]: prev[folderId] === undefined ? false : !prev[folderId]
        }));
    }, []);
    const toggleFolderCollapsed = useCallback((folderId) => {
        setFolderCollapsed(prev => ({
            ...prev,
            [folderId]: prev[folderId] === undefined ? true : !prev[folderId]
        }));
    }, []);
    const toggleArtifactVisibility = useCallback((artifactId) => {
        setArtifactVisibility(prev => ({
            ...prev,
            [artifactId]: !prev[artifactId]
        }));
    }, []);
    const setArtifactVisibilityFn = useCallback((artifactId, visible) => {
        setArtifactVisibility(prev => ({
            ...prev,
            [artifactId]: visible
        }));
    }, []);
    const setArtifactOpacityFn = useCallback((artifactId, opacity) => {
        setArtifactOpacity(prev => ({
            ...prev,
            [artifactId]: opacity
        }));
    }, []);
    const isArtifactVisibleFn = useCallback((artifactId) => {
        // Read through derivedVisibility so callers see the same cascaded result
        // the map renderer uses. Falls back to the raw per-artifact toggle when
        // the artifact isn't in the derived map yet (e.g. mid-fetch).
        const derived = derivedVisibility.get(artifactId);
        if (derived)
            return derived.visible;
        return artifactVisibility[artifactId] === true;
    }, [derivedVisibility, artifactVisibility]);
    const getArtifactOpacity = useCallback((artifactId) => {
        var _a;
        return (_a = artifactOpacity[artifactId]) !== null && _a !== void 0 ? _a : 1.0;
    }, [artifactOpacity]);
    const resetAllArtifactVisibility = useCallback(() => {
        setArtifactVisibility({});
    }, []);
    const restoreUserArtifactVisibility = useCallback((configs) => {
        var _a, _b;
        const configMap = new Map(configs.map(c => [c.artifactId, c]));
        // Build new visibility state: specified artifacts get config value, others get false
        const allArtifactIds = folders.flatMap(f => f.artifacts.map(a => a.id));
        const newVisibility = {};
        const newOpacity = {};
        for (const id of allArtifactIds) {
            const config = configMap.get(id);
            newVisibility[id] = (_a = config === null || config === void 0 ? void 0 : config.visible) !== null && _a !== void 0 ? _a : false;
            newOpacity[id] = (_b = config === null || config === void 0 ? void 0 : config.opacity) !== null && _b !== void 0 ? _b : 1.0;
        }
        setArtifactVisibility(newVisibility);
        setArtifactOpacity(newOpacity);
    }, [folders]);
    const updateArtifactName = useCallback(async (artifactId, name) => {
        await userFolderService.updateArtifact({ artifactId, name });
        setFolders(prev => prev.map(folder => ({
            ...folder,
            artifacts: folder.artifacts.map(a => a.id === artifactId ? { ...a, name } : a)
        })));
    }, []);
    const updateArtifactShowLabel = useCallback(async (artifact, showLabel) => {
        const geometryBased = artifact.artifactType === 'measurement' || artifact.artifactType === 'measurement_circle';
        if (geometryBased) {
            // Measurements/circles read showLabel from geometry_json — rewrite it.
            let geometryJson = artifact.geometryJson;
            try {
                const g = JSON.parse(artifact.geometryJson);
                g.showLabel = showLabel;
                geometryJson = JSON.stringify(g);
            }
            catch (_a) {
                return; // malformed geometry; nothing safe to write
            }
            await userFolderService.updateArtifact({ artifactId: artifact.id, geometryJson });
            setFolders(prev => prev.map(folder => ({
                ...folder,
                artifacts: folder.artifacts.map(a => a.id === artifact.id ? { ...a, geometryJson } : a)
            })));
        }
        else {
            // Placemarks/labels read showLabel from the show_label column.
            await userFolderService.updateArtifact({ artifactId: artifact.id, showLabel });
            setFolders(prev => prev.map(folder => ({
                ...folder,
                artifacts: folder.artifacts.map(a => a.id === artifact.id ? { ...a, showLabel } : a)
            })));
        }
    }, []);
    const duplicateArtifact = useCallback(async (artifact) => {
        // Bypass createArtifact (which appends to the folder end) — we want the
        // copy to land directly below the source so the user sees it next to the
        // original. Insert locally at originalIdx+1, then persist the new order.
        const created = await userFolderService.createArtifact({
            folderId: artifact.folderId,
            artifactType: artifact.artifactType,
            name: `${artifact.name} (copy)`,
            color: artifact.color,
            displayUnit: artifact.displayUnit,
            geometryJson: artifact.geometryJson,
            icon: artifact.icon,
            showLabel: artifact.showLabel,
        });
        let nextOrder = [];
        setFolders(prev => prev.map(folder => {
            if (folder.id !== artifact.folderId)
                return folder;
            const idx = folder.artifacts.findIndex(a => a.id === artifact.id);
            const insertAt = idx >= 0 ? idx + 1 : folder.artifacts.length;
            const next = [...folder.artifacts.slice(0, insertAt), created, ...folder.artifacts.slice(insertAt)];
            nextOrder = next.map(a => a.id);
            return { ...folder, artifacts: next };
        }));
        setArtifactVisibility(prev => ({ ...prev, [created.id]: true }));
        if (nextOrder.length) {
            try {
                await userFolderService.reorderArtifacts(artifact.folderId, nextOrder);
            }
            catch (err) {
                console.error('Failed to persist duplicate insert position:', err);
            }
        }
        return created;
    }, []);
    const reorderFolders = useCallback(async (folderIds, parentFolderId) => {
        if (!projectId)
            return;
        // Optimistic local reorder: only touch the sortOrder of folders inside the
        // named sibling set. Other siblings (and folders under different parents)
        // are left untouched. This matches what the server does and keeps the flat
        // `folders` array consistent with the sortOrder values.
        const scopeKey = parentFolderId !== null && parentFolderId !== void 0 ? parentFolderId : null;
        setFolders(prev => {
            const byId = new Map(prev.map(f => [f.id, f]));
            const requestedSet = new Set(folderIds);
            // Build a new sortOrder mapping just for the IDs in this sibling set.
            const newSortOrderByID = new Map();
            folderIds.forEach((id, idx) => newSortOrderByID.set(id, idx));
            return prev.map(f => {
                var _a, _b;
                const inScope = ((_a = f.parentFolderId) !== null && _a !== void 0 ? _a : null) === scopeKey && requestedSet.has(f.id);
                if (!inScope)
                    return f;
                return { ...f, sortOrder: (_b = newSortOrderByID.get(f.id)) !== null && _b !== void 0 ? _b : f.sortOrder };
            });
        });
        try {
            await userFolderService.reorderFolders(projectId, folderIds, parentFolderId !== null && parentFolderId !== void 0 ? parentFolderId : undefined);
        }
        catch (err) {
            console.error('Failed to persist folder reorder:', err);
        }
    }, [projectId]);
    const moveFolder = useCallback(async (folderId, newParentFolderId) => {
        // Optimistic re-parent: update parentFolderId locally and reset sortOrder
        // to land at the bottom of the destination's children. If the server
        // rejects (cycle, depth > 3), the next fetch reconciles.
        setFolders(prev => {
            var _a;
            let nextSortOrder = 0;
            for (const f of prev) {
                if (((_a = f.parentFolderId) !== null && _a !== void 0 ? _a : null) === (newParentFolderId !== null && newParentFolderId !== void 0 ? newParentFolderId : null) && f.id !== folderId) {
                    nextSortOrder = Math.max(nextSortOrder, f.sortOrder + 1);
                }
            }
            return prev.map(f => f.id === folderId
                ? { ...f, parentFolderId: newParentFolderId !== null && newParentFolderId !== void 0 ? newParentFolderId : undefined, sortOrder: nextSortOrder }
                : f);
        });
        try {
            await userFolderService.updateFolder({ folderId, parentFolderId: newParentFolderId });
        }
        catch (err) {
            console.error('Failed to persist folder move:', err);
            // Reconcile by re-fetching so the rejected move snaps back.
            await fetchFolders();
        }
    }, [fetchFolders]);
    const moveArtifact = useCallback(async (artifactId, newFolderId) => {
        // Optimistic local move: pull the artifact out of its current folder, push
        // onto the end of the target. Server-side update happens after — backend
        // validates target folder is same-project and resets sort_order. If the
        // server rejects, on next folder fetch the local state will reconcile.
        let movedArtifact = null;
        setFolders(prev => {
            const next = prev.map(f => {
                const idx = f.artifacts.findIndex(a => a.id === artifactId);
                if (idx === -1)
                    return f;
                movedArtifact = { ...f.artifacts[idx], folderId: newFolderId };
                return { ...f, artifacts: f.artifacts.filter(a => a.id !== artifactId) };
            });
            if (!movedArtifact)
                return prev;
            return next.map(f => f.id === newFolderId
                ? { ...f, artifacts: [...f.artifacts, movedArtifact] }
                : f);
        });
        await userFolderService.updateArtifact({ artifactId, folderId: newFolderId });
    }, []);
    return {
        folders,
        folderTree,
        isLoading: isLoading || settling,
        error: settling ? null : error,
        fetchFolders,
        createFolder,
        updateFolder,
        deleteFolder,
        moveFolder,
        createArtifact,
        deleteArtifact,
        reorderArtifacts,
        folderVisibility,
        folderCollapsed,
        toggleFolderVisibility,
        toggleFolderCollapsed,
        isAncestorHidden,
        artifactVisibility,
        artifactOpacity,
        toggleArtifactVisibility,
        setArtifactVisibility: setArtifactVisibilityFn,
        setArtifactOpacity: setArtifactOpacityFn,
        isArtifactVisible: isArtifactVisibleFn,
        getArtifactOpacity,
        resetAllArtifactVisibility,
        restoreUserArtifactVisibility,
        derivedVisibility,
        updateArtifactName,
        updateArtifactShowLabel,
        duplicateArtifact,
        moveArtifact,
        reorderFolders
    };
};
